import { useEffect, useLayoutEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { formatNumber, formatDateTime } from '@/utils/format';
import { clientProfitabilityApi } from '@/api/services/clientProfitability';
import { toCpInput, type CpFilterState } from './ClientProfitabilityFilterBar';
import type { ClientProfitabilityFilterInput } from '@/api/types/riskDashboard';
import type {
  ClientProfitabilityRowDto,
  ClosedTradeDto,
  DealDto,
  OpenPositionDto,
} from '@/api/types/clientProfitability';

const n = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '0.00' : formatNumber(v, 2));
const price = (v: number | null | undefined, digits?: number | null) => (v == null ? '' : formatNumber(v, digits ?? 5));
const dt = (v: string | null | undefined) => formatDateTime(v);

type SectionKey = 'closed' | 'deals' | 'open' | 'lp';

export function ClientProfitabilitySubTables({
  row,
  applied,
  requestGuid,
}: {
  row: ClientProfitabilityRowDto;
  applied: CpFilterState;
  requestGuid: string;
}) {
  const byUid = applied.byUniqueId;
  const live = applied.liveData;
  const showClosed = byUid ? row.isMt4 : !row.isMt5;
  const showDeals = byUid ? row.isMt5 : row.isMt5;

  // Row-scoped filter (login+datasource or uniqueId), reset the hide-state per row.
  const base = toCpInput(applied, requestGuid);
  const input = byUid
    ? { ...base, uniqueId: row.uniqueId, byUniqueId: true, login: null }
    : { ...base, login: row.login, dataSourceId: row.platformId, uniqueId: null, byUniqueId: false };

  const [hidden, setHidden] = useState<Set<SectionKey>>(new Set());
  const rowKey = `${row.platformId}:${byUid ? row.uniqueId : row.login}`;
  useEffect(() => setHidden(new Set()), [rowKey]);
  const hide = (k: SectionKey) => setHidden((prev) => new Set(prev).add(k));
  const visible = (k: SectionKey) => !hidden.has(k);

  // Scroll to the first visible trades section when a row is shown (client-
  // profitability-tables.js handleShowTrades → animate scrollTop offset -80).
  useLayoutEffect(() => {
    const firstId = showClosed
      ? 'closed-trades-section'
      : showDeals
        ? 'deals-section'
        : live
          ? 'open-positions-section'
          : null;
    if (!firstId) return;
    const el = document.getElementById(firstId);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
  }, [rowKey, showClosed, showDeals, live]);

  const closed = useQuery({
    queryKey: ['cp-closed', rowKey, input],
    queryFn: () => clientProfitabilityApi.getHistoryTrades(input),
    enabled: showClosed && visible('closed'),
  });
  // Deals — true server-side paging (legacy dealsTable serverSide: true). The
  // row-scoped filter is merged into every page request via extraParams; skip/
  // take/draw come from the controller.
  const deals = useServerDataTable<DealDto, ClientProfitabilityFilterInput>({
    queryKey: ['cp-deals', rowKey],
    fetcher: (req) => clientProfitabilityApi.getDeals(req),
    pageSize: 5,
    extraParams: input,
    enabled: showDeals && visible('deals'),
  });
  const openPos = useQuery({
    queryKey: ['cp-open', rowKey, input],
    queryFn: () => clientProfitabilityApi.getTodayTrades(input),
    enabled: live && visible('open'),
  });

  const closedCols: DataGridColumn<ClosedTradeDto>[] = [
    { key: 'ticket', title: 'Ticket', value: (r) => r.ticket },
    { key: 'name', title: 'Name', value: (r) => r.name },
    { key: 'uniqueId', title: 'Unique Id', value: (r) => r.uniqueId },
    { key: 'login', title: 'Login', value: (r) => r.login },
    { key: 'accCurrency', title: 'Acc Currency', value: (r) => r.accCurrency },
    { key: 'type', title: 'Type', value: (r) => r.type },
    { key: 'volume', title: 'Volume', className: 'text-end', value: (r) => r.volume, cell: (r) => n(r.volume) },
    { key: 'instrument', title: 'Instrument', value: (r) => r.instrument },
    { key: 'openPrice', title: 'Open Price', className: 'text-end', value: (r) => r.openPrice, cell: (r) => price(r.openPrice, r.digits) },
    { key: 'openTime', title: 'Open Time', value: (r) => r.openTime, cell: (r) => dt(r.openTime) },
    { key: 'closePrice', title: 'Close Price', className: 'text-end', value: (r) => r.closePrice ?? null, cell: (r) => price(r.closePrice, r.digits) },
    { key: 'closeTime', title: 'Close Time', value: (r) => r.closeTime ?? '', cell: (r) => dt(r.closeTime) },
    { key: 'profitRc', title: 'Profit (RC)', className: 'text-end', value: (r) => r.profitRc, cell: (r) => n(r.profitRc) },
    { key: 'commissionRc', title: 'Commission (RC)', className: 'text-end', value: (r) => r.commissionRc, cell: (r) => n(r.commissionRc) },
    { key: 'swapRc', title: 'Swap (RC)', className: 'text-end', value: (r) => r.swapRc, cell: (r) => n(r.swapRc) },
    { key: 'country', title: 'Country', value: (r) => r.country },
    { key: 'category', title: 'Category', value: (r) => r.category },
    { key: 'connector', title: 'Connector', value: (r) => r.connector },
  ];

  const dealCols: DataGridColumn<DealDto>[] = [
    { key: 'time', title: 'Time', value: (r) => r.time, cell: (r) => dt(r.time) },
    { key: 'name', title: 'Name', value: (r) => r.name },
    { key: 'uniqueId', title: 'Unique Id', value: (r) => r.uniqueId },
    { key: 'login', title: 'Login', value: (r) => r.login },
    { key: 'accCurrency', title: 'Acc Currency', value: (r) => r.accCurrency },
    { key: 'deal', title: 'Deal', value: (r) => r.ticket },
    { key: 'positionId', title: 'Position Id', value: (r) => r.positionId },
    { key: 'instrument', title: 'Instrument', value: (r) => r.instrument },
    { key: 'entry', title: 'Entry', value: (r) => r.entry },
    { key: 'openTime', title: 'Open Time', value: (r) => r.openTime ?? '', cell: (r) => dt(r.openTime) },
    { key: 'volume', title: 'Volume', className: 'text-end', value: (r) => r.volume, cell: (r) => n(r.volume) },
    { key: 'price', title: 'Price', className: 'text-end', value: (r) => r.price, cell: (r) => price(r.price, r.digits) },
    { key: 'commissionRc', title: 'Commission (RC)', className: 'text-end', value: (r) => r.commissionRc, cell: (r) => n(r.commissionRc) },
    { key: 'swapRc', title: 'Swap (RC)', className: 'text-end', value: (r) => r.swapRc, cell: (r) => n(r.swapRc) },
    { key: 'profitRc', title: 'Profit (RC)', className: 'text-end', value: (r) => r.profitRc, cell: (r) => n(r.profitRc) },
    { key: 'connector', title: 'Connector', value: (r) => r.connector },
  ];

  const openCols: DataGridColumn<OpenPositionDto>[] = [
    { key: 'instrument', title: 'Symbol', value: (r) => r.instrument },
    { key: 'ticket', title: 'Ticket', value: (r) => r.ticket },
    { key: 'name', title: 'Name', value: (r) => r.name },
    { key: 'uniqueId', title: 'Unique Id', value: (r) => r.uniqueId },
    { key: 'login', title: 'Login', value: (r) => r.login },
    { key: 'accCurrency', title: 'Acc Currency', value: (r) => r.accCurrency },
    { key: 'openTime', title: 'Open Time', value: (r) => r.openTime, cell: (r) => dt(r.openTime) },
    { key: 'type', title: 'Type', value: (r) => r.type },
    { key: 'volume', title: 'Volume', className: 'text-end', value: (r) => r.volume, cell: (r) => n(r.volume) },
    { key: 'openPrice', title: 'Open Price', className: 'text-end', value: (r) => r.openPrice, cell: (r) => price(r.openPrice, r.digits) },
    { key: 'swapRc', title: 'Swap (RC)', className: 'text-end', value: (r) => r.swapRc, cell: (r) => n(r.swapRc) },
    { key: 'profitRc', title: 'Profit (RC)', className: 'text-end', value: (r) => r.profitRc, cell: (r) => n(r.profitRc) },
    { key: 'commissionRc', title: 'Commission (RC)', className: 'text-end', value: (r) => r.commissionRc, cell: (r) => n(r.commissionRc) },
    { key: 'country', title: 'Country', value: (r) => r.country },
    { key: 'category', title: 'Category', value: (r) => r.category },
    { key: 'connector', title: 'Connector', value: (r) => r.connector },
  ];

  return (
    <div id="cp-trades-sections">
      {showClosed && visible('closed') && (
        <Section id="closed-trades-section" title="Closed Trades" onHide={() => hide('closed')}>
          <DataGrid columns={closedCols} data={closed.data?.items ?? []} rowKey={(r) => String(r.ticket)} includeExcel excelFileName="closed-trades" emptyMessage={closed.isFetching ? 'Loading…' : 'No closed trades.'} />
        </Section>
      )}
      {showDeals && visible('deals') && (
        <Section id="deals-section" title="Deals" onHide={() => hide('deals')}>
          <DataGrid
            columns={dealCols}
            data={deals.rows}
            rowKey={(r) => String(r.id)}
            includeExcel
            excelFileName="deals"
            emptyMessage={deals.isLoading ? 'Loading…' : 'No deals.'}
            server={{
              pageIndex: deals.pageIndex,
              pageSize: deals.pageSize,
              pageCount: deals.pageCount,
              totalCount: deals.totalCount,
              onPageChange: deals.setPageIndex,
              onPageSizeChange: deals.setPageSize,
            }}
          />
        </Section>
      )}
      {live && visible('open') && (
        <Section id="open-positions-section" title="Open Positions" onHide={() => hide('open')}>
          <DataGrid columns={openCols} data={openPos.data?.items ?? []} rowKey={(r) => String(r.ticket)} includeExcel excelFileName="open-positions" emptyMessage={openPos.isFetching ? 'Loading…' : 'No open positions.'} />
        </Section>
      )}
      {/* LP Trades placeholder (_SubTables.cshtml lp-trades-section). Hidden by
          default like legacy, but kept so the section + close button exist. */}
      {visible('lp') && (
        <Section id="lp-trades-section" title="LP Trades" onHide={() => hide('lp')} hidden>
          <p className="text-muted">LP trades view is not available yet. This feature will be supported soon.</p>
        </Section>
      )}
    </div>
  );
}

function Section({
  id,
  title,
  onHide,
  hidden,
  children,
}: {
  id: string;
  title: string;
  onHide: () => void;
  hidden?: boolean;
  children: ReactNode;
}) {
  return (
    <div id={id} className={`card custom-card mt-3${hidden ? ' d-none' : ''}`}>
      <div className="card-header d-flex justify-content-between align-items-center border-0">
        <h6 className="mb-0 sub-table-title">{title}</h6>
        <button type="button" className="btn btn-sm" title="Close" onClick={onHide}>
          <i className="fa fa-times" />
        </button>
      </div>
      <div className="card-body p-0">{children}</div>
    </div>
  );
}
